package com.eladfinish.bible.contest.ui.common

import androidx.compose.foundation.Image
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.bible_search_json
import com.eladfinish.bible.contest.data.common.AssetReader
import com.eladfinish.bible.contest.di.getKoinInstance
import io.github.alexzhirkevich.compottie.Compottie
import io.github.alexzhirkevich.compottie.LottieCompositionSpec
import io.github.alexzhirkevich.compottie.animateLottieCompositionAsState
import io.github.alexzhirkevich.compottie.rememberLottieComposition
import io.github.alexzhirkevich.compottie.rememberLottiePainter

/**
 * Seamless-looping "searching the Tanakh" Lottie animation (Android + iOS).
 *
 * The loader below runs exactly once per appearance: `rememberLottieComposition`
 * keys its `LaunchedEffect` on a stable result, and Compottie already runs the
 * loader on its IO dispatcher (so no main-thread blocking) and caches the parsed
 * composition by content hash across openings. Rendering happens on the Compose
 * render thread, so it never competes with the search (which runs on IoDispatcher).
 *
 * Asset reading is resolved lazily inside the loader; if it fails (e.g. an IDE
 * @Preview with no Koin graph) the animation simply stays blank instead of crashing.
 */
@Composable
fun BibleSearchAnimation(
    modifier: Modifier = Modifier,
) {
    val composition by rememberLottieComposition {
        val assetReader = getKoinInstance<AssetReader>()
        val json = assetReader.readFile(SharedRes.assets.animations.bible_search_json)
        LottieCompositionSpec.JsonString(json)
    }

    val progress by animateLottieCompositionAsState(
        composition = composition,
        iterations = Compottie.IterateForever,
    )

    Image(
        painter = rememberLottiePainter(
            composition = composition,
            progress = { progress },
        ),
        contentDescription = null,
        modifier = modifier,
        contentScale = ContentScale.Fit,
    )
}
